import React, { useState, useEffect } from 'react';
import '../scss/QuoteForm.scss';

const QuoteForm = ({ onSubmit, tyreData, isSubmitting = false, disabled = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    postalCode: '',
    city: '',
    phone: '',
    email: '',
    quantity: 2, // Valeur par défaut
    privacyPolicy: false,
    newsletter: false
  });

  const [errors, setErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Mettre à jour la quantité quand tyreData change
  useEffect(() => {
    if (tyreData?.quantity) {
      setFormData(prev => ({
        ...prev,
        quantity: tyreData.quantity
      }));
    }
  }, [tyreData]);

  // Fonction pour gérer le changement de quantité
  const handleQuantityChange = (newQuantity) => {
    let quantity = parseInt(newQuantity) || 2;
    // Assurer que la quantité est paire et >= 2
    if (quantity < 2) quantity = 2;
    if (quantity % 2 !== 0) quantity = quantity + 1; // Arrondir au nombre pair supérieur
    if (quantity > 98) quantity = 98; // Maximum pair
    
    setFormData(prev => ({
      ...prev,
      quantity: quantity
    }));
  };

  // Fonction pour incrémenter/décrémenter la quantité par 2
  const adjustQuantity = (delta) => {
    const newQuantity = formData.quantity + (delta * 2); // Incrément/décrément par 2
    handleQuantityChange(newQuantity);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Effacer l'erreur quand l'utilisateur commence à taper
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'Le prénom est requis';
    if (!formData.lastName.trim()) newErrors.lastName = 'Le nom est requis';
    if (!formData.address.trim()) newErrors.address = 'L\'adresse est requise';
    if (!formData.postalCode.trim()) newErrors.postalCode = 'Le code postal est requis';
    if (!formData.city.trim()) newErrors.city = 'La ville est requise';
    if (!formData.phone.trim()) newErrors.phone = 'Le téléphone est requis';
    if (!formData.email.trim()) newErrors.email = 'L\'email est requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email invalide';
    if (formData.quantity < 2) newErrors.quantity = 'La quantité minimum est de 2';
    if (!formData.privacyPolicy) newErrors.privacyPolicy = 'Vous devez accepter la charte de confidentialité';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await onSubmit(formData);
        setSubmitSuccess(true);
        // Masquer le message après 10 secondes
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 10000);
      } catch (error) {
        // Gérer l'erreur si nécessaire
        console.error('Erreur lors de l\'envoi du devis:', error);
      }
    }
  };

  return (
    <form className="quote-form" onSubmit={handleSubmit}>
	 <div className="quote-form-title">Votre demande de devis gratuit</div>
      
      {submitSuccess && (
        <div className="quote-success-message">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#000" strokeWidth="2" fill="none"/>
            <path d="M8 12l2 2 4-4" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Votre demande de devis a bien été envoyée ! Nous vous recontacterons dans les plus brefs délais.</span>
        </div>
      )}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="lastName">Nom <span className="required">*</span></label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={errors.lastName ? 'error' : ''}
            disabled={disabled || isSubmitting}
          />
          {errors.lastName && <span className="error-message">{errors.lastName}</span>}
        </div>
        
        <div className="form-group">
          <label htmlFor="firstName">Prénom <span className="required">*</span></label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={errors.firstName ? 'error' : ''}
            disabled={disabled || isSubmitting}
          />
          {errors.firstName && <span className="error-message">{errors.firstName}</span>}
        </div>
      </div>

      <div className="form-row three-columns">
		<div className="form-group">
			<label htmlFor="address">Adresse <span className="required">*</span></label>
			<input
			type="text"
			id="address"
			name="address"
			value={formData.address}
			onChange={handleChange}
			className={errors.address ? 'error' : ''}
			disabled={disabled || isSubmitting}
			/>
			{errors.address && <span className="error-message">{errors.address}</span>}
		</div>

        <div className="form-group">
          <label htmlFor="postalCode">Code postal <span className="required">*</span></label>
          <input
            type="text"
            id="postalCode"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleChange}
            className={errors.postalCode ? 'error' : ''}
            disabled={disabled || isSubmitting}
          />
          {errors.postalCode && <span className="error-message">{errors.postalCode}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="city">Ville <span className="required">*</span></label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            className={errors.city ? 'error' : ''}
            disabled={disabled || isSubmitting}
          />
          {errors.city && <span className="error-message">{errors.city}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="phone">Téléphone <span className="required">*</span></label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={errors.phone ? 'error' : ''}
            disabled={disabled || isSubmitting}
          />
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">E-mail <span className="required">*</span></label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? 'error' : ''}
            disabled={disabled || isSubmitting}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>
      </div>

      {/* Nouveau champ quantité */}
      <div className="form-group quantity-form-group">
        <label htmlFor="quantity">Quantité de pneu souhaitée <span className="required">*</span></label>
        <div className="quantity-selector-form">
          <button 
            type="button"
            className="quantity-btn minus"
            onClick={() => adjustQuantity(-1)}
            disabled={formData.quantity <= 2 || disabled || isSubmitting}
          >
            −
          </button>
          <input
            type="number"
            id="quantity"
            name="quantity"
            min="2"
            max="98"
            step="2"
            value={formData.quantity}
            onChange={(e) => handleQuantityChange(e.target.value)}
            className={`quantity-input ${errors.quantity ? 'error' : ''}`}
            disabled={disabled || isSubmitting}
          />
          <button 
            type="button"
            className="quantity-btn plus"
            onClick={() => adjustQuantity(1)}
            disabled={formData.quantity >= 98 || disabled || isSubmitting}
          >
            +
          </button>
        </div>
        {errors.quantity && <span className="error-message">{errors.quantity}</span>}
      </div>

      <div className="form-group checkbox-group">
        <label className="checkbox-container">
          <input
            type="checkbox"
            name="privacyPolicy"
            checked={formData.privacyPolicy}
            onChange={handleChange}
            disabled={disabled || isSubmitting}
          />
          <span className="checkbox-text">
            En validant ce formulaire, je confirme avoir lu et accepté {' '}
            <a href="https://www.eurotyre.fr/charte-de-confidentialite/#devis" target="_blank" rel="noopener noreferrer">
				la charte de confidentialité{' '}
            </a>  Eurotyre *
          </span>
        </label>
        {errors.privacyPolicy && <span className="error-message">{errors.privacyPolicy}</span>}
      </div>

      <div className="form-group checkbox-group">
        <label className="checkbox-container">
          <input
            type="checkbox"
            name="newsletter"
            checked={formData.newsletter}
            onChange={handleChange}
            disabled={disabled || isSubmitting}
          />
          <span className="checkbox-text">
            Je souhaite m'inscrire à la newsletter Eurotyre
          </span>
        </label>
      </div>
	  <div className="text-center">
		<button type="submit" className="submit-button" disabled={disabled || isSubmitting}>
			{isSubmitting ? 'Envoi en cours...' : 'Obtenir mon devis personnalisé'}
		</button>
	  </div>
    </form>
  );
};

export default QuoteForm; 